import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Avatar } from "@/components/Avatar";
import { Pill } from "@/components/Pill";
import { Icon } from "@/components/Icon";
import { toast } from "@/components/Toast";
import { Skeleton } from "@/components/Skeleton";

/**
 * MembersTable — shared between /admin/users and /settings/members.
 * Fetches users + role data itself so it can be dropped into any shell.
 * Renders an admin-only empty state if the current user is not an admin.
 */
export function MembersTable() {
  const me = useQuery({ queryKey: queryKeys.me(), queryFn: api.me });
  const users = useQuery({
    queryKey: queryKeys.adminUsers(),
    queryFn: api.adminListUsers,
    retry: false,
  });
  const qc = useQueryClient();
  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: "admin" | "member" }) =>
      api.adminSetRole(id, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.adminUsers() });
      qc.invalidateQueries({ queryKey: queryKeys.me() });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "last_admin") {
        toast.error("Can't demote the last admin. Promote someone else first.");
      } else {
        toast.error("Couldn't change role. Try again.");
      }
    },
  });

  if (users.isError || (me.data && me.data.user.role !== "admin")) {
    return (
      <div className="empty" style={{ padding: 60 }}>
        <div className="e-art">
          <Icon name="key" size={22} />
        </div>
        <h3 style={{ margin: 0 }}>Admin access required.</h3>
        <p className="t-sm">
          Only workspace admins can invite people or change roles. Ask an admin
          to promote your account from this page — roles are managed here, not
          on your profile.
        </p>
      </div>
    );
  }

  if (users.isLoading || !users.data || !me.data) {
    return (
      <div style={{ padding: 30 }}>
        <Skeleton count={8} height={44} />
      </div>
    );
  }

  const rows = users.data.users;
  const adminCount = users.data.adminCount;
  const myId = me.data.user.id;

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <p className="t-sm" style={{ margin: 0, opacity: 0.75 }}>
          {rows.length} users · {adminCount} admin
          {adminCount === 1 ? "" : "s"}
        </p>
      </div>
      <div
        className="panel"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r-lg)",
        }}
      >
        {rows.map((u) => {
          const isLastAdmin = u.role === "admin" && adminCount <= 1;
          const isSelf = u.id === myId;
          const disabled = isLastAdmin;
          const tooltip = isLastAdmin
            ? "Cannot demote — last admin"
            : isSelf
              ? "You can change your own role"
              : undefined;
          return (
            <div
              key={u.id}
              className="task-row"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
              }}
            >
              <Avatar
                id={u.id}
                name={u.name || u.email}
                size="md"
                image={u.image}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="tr-title">{u.name || u.email}</div>
                {u.name && (
                  <div className="t-xs" style={{ opacity: 0.7 }}>
                    {u.email}
                  </div>
                )}
              </div>
              <Pill variant={u.role === "admin" ? "doing" : undefined} dot>
                {u.role === "admin" ? "Admin" : "Member"}
              </Pill>
              <select
                className="select"
                value={u.role}
                disabled={disabled || setRole.isPending}
                title={tooltip}
                onChange={(e) =>
                  setRole.mutate({
                    id: u.id,
                    role: e.target.value as "admin" | "member",
                  })
                }
                // .select carries `width:100%` (because-ui.css); inside this
                // flex row that gives it a full-width flex-basis, collapsing the
                // name column to 0 and overlapping the role pill. Pin a fixed
                // width and stop it growing/shrinking.
                style={{ width: 130, flexShrink: 0 }}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          );
        })}
      </div>
    </>
  );
}
